/**
 * admin/index.tsx — Admin landing.
 *
 * Lives at `/admin`. Hard-gated: only users with `role === 'admin'` pass the
 * `beforeLoad` guard; everyone else is redirected appropriately.
 */

import type { ReactNode } from 'react';
import { createFileRoute, redirect, Link } from '@tanstack/react-router';
import { PageHeader } from '../../components/ui/PageHeader.js';
import { StatePanel } from '../../components/ui/StatePanel.js';
import { freshSessionQuery, type SessionPrincipal } from '../../lib/api/queries.js';

export const Route = createFileRoute('/admin/')({
  beforeLoad: async ({ context, location }) => {
    const session = await context.queryClient.fetchQuery(freshSessionQuery);
    const user = session?.ok ? (session.data?.user ?? null) : null;
    if (!user) {
      throw redirect({
        to: '/login',
        search: { error: undefined, redirect: location.href },
      });
    }
    if (user.role === 'tutor' && user.isApprovedTutor) {
      throw redirect({ to: '/dashboard' });
    }
    if (user.role === 'tutor' || user.kind === 'student') {
      throw redirect({ to: '/me' });
    }
    if (user.role !== 'admin') {
      throw redirect({ to: '/' });
    }
    return null;
  },
  component: AdminDashboard,
});

function AdminDashboard(): ReactNode {
  return (
    <main className="page-shell">
      <PageHeader
        eyebrow="Operasional"
        title="Operasional portal"
        description="Pilih aksi yang perlu kamu tangani hari ini. Manajemen tutor dan siswa menjadi prioritas operasional."
      />

      <section className="section" aria-labelledby="aksi-queue">
        <h2 className="section-title" id="aksi-queue">
          Antrian aksi
        </h2>
        <div className="card-grid">
          <Link className="card" to="/admin/tutors">
            <span className="card-meta">Manajemen</span>
            <h3 className="card-title">Manajemen tutor</h3>
            <p>
              Buat akun tutor baru, nonaktifkan tutor yang sudah tidak aktif, dan lihat daftar tutor
              terdaftar.
            </p>
          </Link>
          <Link className="card" to="/admin/students">
            <span className="card-meta">Manajemen</span>
            <h3 className="card-title">Daftar siswa</h3>
            <p>
              Tambah siswa secara manual, lihat progres per siswa, dan nonaktifkan akun bila
              diperlukan.
            </p>
          </Link>
          <Link className="card" to="/admin/parents" data-testid="orang-tua-card">
            <span className="card-meta">Manajemen</span>
            <h3 className="card-title">Manajemen orang tua</h3>
            <p>Buat akun orang tua, tautkan dengan siswa, dan kelola status aktif/nonaktif.</p>
          </Link>
          <Link className="card" to="/admin/sync">
            <span className="card-meta">Operasional</span>
            <h3 className="card-title">Sinkronisasi registry</h3>
            <p>Pantau dan picu sinkronisasi antara `coding-games` dan R2.</p>
          </Link>
          <Link className="card" to="/admin/quizzes" data-testid="admin-menu-quizzes">
            <span className="card-meta">Konten</span>
            <h3 className="card-title">Kelola Quiz</h3>
            <p>Library quiz + review submission kustom dari tutor.</p>
          </Link>
        </div>
      </section>

      <section className="section" aria-labelledby="activity-section">
        <h2 className="section-title" id="activity-section">
          Pemantauan
        </h2>
        <Link className="card" to="/admin/audit" data-testid="activity-card">
          <span className="card-meta">Audit</span>
          <h3 className="card-title">Log aktivitas</h3>
          <p>
            Riwayat otomatis aksi admin dan tutor — setujui tutor, buat kode undangan, dan tukar
            kode.
          </p>
        </Link>
        <StatePanel
          variant="disabled"
          title="Pemantauan lanjutan akan segera tersedia"
          description="Metrik operasional (CHP, total siswa, dsb.) sedang dalam pengembangan."
          illustrationSrc="/images/generated/admin-empty/admin-empty.webp"
          illustrationAlt="Tempat kosong untuk log aktivitas"
        />
      </section>
    </main>
  );
}
